import React from 'react'
import {
    Box,
    Button,
    IconButton,
    MenuItem,
    Select,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'

import { useSignableDocs } from '../../hooks/Outreach/useSignableDocs'
import {
    QUALIFYING_METHOD_IDS,
    QUALIFYING_METHOD_META,
    QualifyingMethodId,
} from './conditionalApprovals'
import { FormSaveAction } from './FormSaveButton'
import { SetupArtifactShell } from './SetupArtifactShell'
import { SelectedSetupConfig, SetupConfigOption } from './setupArtifacts'
import {
    LeasePacketMember,
    LeasePacketRule,
    PACKET_CONDITION_FIELDS,
    PacketRuleAction,
    PacketWhenKind,
    createLeasePacketMember,
    leasePacketReady,
    packetFieldAnswerChoices,
    ruleWhenKind,
    signablePacketsReadiness,
} from './signableDocRoles'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.25),
        width: '100%',
        padding: theme.spacing(0.5, 0, 1),
    },
    intro: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.45,
    },
    summary: {
        fontSize: '0.75rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
    },
    sectionTitle: {
        fontWeight: 700,
        fontSize: '0.8125rem',
        marginTop: theme.spacing(0.5),
    },
    sectionHint: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.4,
        marginTop: -4,
    },
    packetRow: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1.25, 1.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        backgroundColor: theme.palette.background.paper,
    },
    packetOrder: {
        fontSize: '0.75rem',
        fontWeight: 700,
        color: theme.palette.text.secondary,
        width: 22,
        flexShrink: 0,
    },
    packetSelect: {
        minWidth: 180,
        flex: '1 1 180px',
        fontSize: '0.8125rem',
    },
    actionSelect: {
        minWidth: 110,
        fontSize: '0.8125rem',
    },
    whenSelect: {
        minWidth: 160,
        fontSize: '0.8125rem',
    },
    fieldSelect: {
        minWidth: 180,
        flex: '1 1 160px',
        fontSize: '0.8125rem',
    },
    equalsSelect: {
        minWidth: 100,
        fontSize: '0.8125rem',
    },
    packetActions: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: 'auto',
    },
    addRow: {
        alignSelf: 'flex-start',
    },
}))

function ruleFromWhenKind(
    kind: PacketWhenKind,
    previous: LeasePacketRule,
): LeasePacketRule {
    const action = previous.action
    if (kind === 'always') return { type: 'always', action }
    if (kind === 'qualifying_method') {
        const methodId =
            previous.type === 'when_qualifying_method'
                ? previous.methodId
                : 'personal_guarantor'
        return { type: 'when_qualifying_method', action, methodId }
    }
    const fieldId =
        previous.type === 'when_answer' ? previous.fieldId : 'parking_needed'
    const choices = packetFieldAnswerChoices(fieldId)
    const equals =
        previous.type === 'when_answer'
            ? previous.equals
            : choices?.[0] ?? 'Yes'
    return { type: 'when_answer', action, fieldId, equals }
}

const PacketEditor: React.FC<{
    members: LeasePacketMember[]
    onChange: (next: LeasePacketMember[]) => void
    readOnly: boolean
    docs: Array<{ id: number; name: string }>
    docsLoading: boolean
    emptyHint: string
    addLabel: string
}> = ({
    members,
    onChange,
    readOnly,
    docs,
    docsLoading,
    emptyHint,
    addLabel,
}) => {
    const classes = useStyles()

    const updateMember = (
        memberId: string,
        patch: Partial<LeasePacketMember>,
    ) => {
        if (readOnly) return
        onChange(
            members.map((member) =>
                member.id === memberId ? { ...member, ...patch } : member,
            ),
        )
    }

    const moveMember = (index: number, delta: number) => {
        if (readOnly) return
        const nextIndex = index + delta
        if (nextIndex < 0 || nextIndex >= members.length) return
        const next = [...members]
        const [row] = next.splice(index, 1)
        next.splice(nextIndex, 0, row)
        onChange(next)
    }

    return (
        <>
            {members.length === 0 ? (
                <Typography className={classes.sectionHint}>
                    {emptyHint}
                </Typography>
            ) : null}

            {members.map((member, index) => {
                const whenKind = ruleWhenKind(member.rule)
                const answerChoices =
                    member.rule.type === 'when_answer'
                        ? packetFieldAnswerChoices(member.rule.fieldId)
                        : null
                return (
                    <Box key={member.id} className={classes.packetRow}>
                        <Typography className={classes.packetOrder}>
                            {index + 1}.
                        </Typography>
                        <Select
                            className={classes.packetSelect}
                            value={member.docId ?? ''}
                            disabled={readOnly || docsLoading}
                            variant="outlined"
                            margin="dense"
                            displayEmpty
                            onChange={(e) =>
                                updateMember(member.id, {
                                    docId:
                                        String(e.target.value) === ''
                                            ? null
                                            : String(e.target.value),
                                })
                            }
                        >
                            <MenuItem value="">
                                <em>Select document…</em>
                            </MenuItem>
                            {docs.map((doc) => (
                                <MenuItem key={doc.id} value={String(doc.id)}>
                                    {doc.name}
                                </MenuItem>
                            ))}
                        </Select>
                        <Select
                            className={classes.actionSelect}
                            value={member.rule.action}
                            disabled={readOnly}
                            variant="outlined"
                            margin="dense"
                            onChange={(e) =>
                                updateMember(member.id, {
                                    rule: {
                                        ...member.rule,
                                        action: String(
                                            e.target.value,
                                        ) as PacketRuleAction,
                                    },
                                })
                            }
                        >
                            <MenuItem value="include">Include</MenuItem>
                            <MenuItem value="exclude">Exclude</MenuItem>
                        </Select>
                        <Select
                            className={classes.whenSelect}
                            value={whenKind}
                            disabled={readOnly}
                            variant="outlined"
                            margin="dense"
                            onChange={(e) =>
                                updateMember(member.id, {
                                    rule: ruleFromWhenKind(
                                        String(
                                            e.target.value,
                                        ) as PacketWhenKind,
                                        member.rule,
                                    ),
                                })
                            }
                        >
                            <MenuItem value="always">Always</MenuItem>
                            <MenuItem value="applicant_data">
                                Applicant Data
                            </MenuItem>
                            <MenuItem value="qualifying_method">
                                Qualifying method
                            </MenuItem>
                        </Select>
                        {member.rule.type === 'when_answer' ? (
                            <>
                                <Select
                                    className={classes.fieldSelect}
                                    value={member.rule.fieldId}
                                    disabled={readOnly}
                                    variant="outlined"
                                    margin="dense"
                                    onChange={(e) => {
                                        const fieldId = String(e.target.value)
                                        const choices = packetFieldAnswerChoices(
                                            fieldId,
                                        )
                                        updateMember(member.id, {
                                            rule: {
                                                ...member.rule,
                                                type: 'when_answer',
                                                fieldId,
                                                equals:
                                                    choices?.[0] ??
                                                    (member.rule.type ===
                                                    'when_answer'
                                                        ? member.rule.equals
                                                        : 'Yes'),
                                            },
                                        })
                                    }}
                                >
                                    {PACKET_CONDITION_FIELDS.map((field) => (
                                        <MenuItem
                                            key={field.id}
                                            value={field.id}
                                        >
                                            {field.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {answerChoices ? (
                                    <Select
                                        className={classes.equalsSelect}
                                        value={member.rule.equals}
                                        disabled={readOnly}
                                        variant="outlined"
                                        margin="dense"
                                        onChange={(e) =>
                                            updateMember(member.id, {
                                                rule: {
                                                    type: 'when_answer',
                                                    action: member.rule.action,
                                                    fieldId:
                                                        member.rule.type ===
                                                        'when_answer'
                                                            ? member.rule
                                                                  .fieldId
                                                            : PACKET_CONDITION_FIELDS[0]
                                                                  ?.id ?? '',
                                                    equals: String(
                                                        e.target.value,
                                                    ),
                                                },
                                            })
                                        }
                                    >
                                        {answerChoices.map((value) => (
                                            <MenuItem key={value} value={value}>
                                                {value}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                ) : (
                                    <TextField
                                        className={classes.equalsSelect}
                                        value={member.rule.equals}
                                        disabled={readOnly}
                                        variant="outlined"
                                        margin="dense"
                                        placeholder="Answer"
                                        onChange={(e) =>
                                            updateMember(member.id, {
                                                rule: {
                                                    type: 'when_answer',
                                                    action: member.rule.action,
                                                    fieldId:
                                                        member.rule.type ===
                                                        'when_answer'
                                                            ? member.rule
                                                                  .fieldId
                                                            : PACKET_CONDITION_FIELDS[0]
                                                                  ?.id ?? '',
                                                    equals: e.target.value,
                                                },
                                            })
                                        }
                                    />
                                )}
                            </>
                        ) : null}
                        {member.rule.type === 'when_qualifying_method' ? (
                            <Select
                                className={classes.fieldSelect}
                                value={member.rule.methodId}
                                disabled={readOnly}
                                variant="outlined"
                                margin="dense"
                                onChange={(e) =>
                                    updateMember(member.id, {
                                        rule: {
                                            ...member.rule,
                                            type: 'when_qualifying_method',
                                            methodId: String(
                                                e.target.value,
                                            ) as QualifyingMethodId,
                                        },
                                    })
                                }
                            >
                                {QUALIFYING_METHOD_IDS.map((methodId) => (
                                    <MenuItem key={methodId} value={methodId}>
                                        {QUALIFYING_METHOD_META[methodId].label}
                                    </MenuItem>
                                ))}
                            </Select>
                        ) : null}
                        <Box className={classes.packetActions}>
                            <IconButton
                                size="small"
                                disabled={readOnly || index === 0}
                                onClick={() => moveMember(index, -1)}
                                aria-label="Move up"
                            >
                                <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                disabled={
                                    readOnly || index === members.length - 1
                                }
                                onClick={() => moveMember(index, 1)}
                                aria-label="Move down"
                            >
                                <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                disabled={readOnly}
                                onClick={() =>
                                    onChange(
                                        members.filter(
                                            (row) => row.id !== member.id,
                                        ),
                                    )
                                }
                                aria-label="Remove"
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                )
            })}

            {!readOnly ? (
                <Button
                    className={classes.addRow}
                    color="primary"
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() =>
                        onChange([
                            ...members,
                            createLeasePacketMember({
                                rule: { type: 'always', action: 'include' },
                            }),
                        ])
                    }
                >
                    {addLabel}
                </Button>
            ) : null}
        </>
    )
}

export const SignableDocsSetTab: React.FC<{
    selected: SelectedSetupConfig | null
    onSelected: (config: SelectedSetupConfig) => void
    onClearSelection: () => void
    existing: SetupConfigOption[]
    onExistingChange: (next: SetupConfigOption[]) => void
    leasePacket: LeasePacketMember[]
    onLeasePacketChange: (next: LeasePacketMember[]) => void
    guarantorPacket: LeasePacketMember[]
    onGuarantorPacketChange: (next: LeasePacketMember[]) => void
    contentLocked?: boolean
    artifactEditable?: boolean
    saveAction?: FormSaveAction
}> = ({
    selected,
    onSelected,
    onClearSelection,
    existing,
    onExistingChange,
    leasePacket,
    onLeasePacketChange,
    guarantorPacket,
    onGuarantorPacketChange,
    contentLocked = false,
    artifactEditable = true,
    saveAction,
}) => {
    const classes = useStyles()
    const readOnly =
        !!selected?.orgMandated || contentLocked || !artifactEditable
    const packetOk = leasePacketReady(leasePacket)
    const { docs, isLoading } = useSignableDocs({ status: 'READY' })

    return (
        <SetupArtifactShell
            kind="signable_docs"
            selected={selected}
            onSelected={onSelected}
            onClearSelection={onClearSelection}
            existing={existing}
            onExistingChange={onExistingChange}
            contentLocked={contentLocked}
            readinessLabel={
                selected
                    ? signablePacketsReadiness(leasePacket, guarantorPacket)
                    : undefined
            }
            saveAction={readOnly ? undefined : saveAction}
        >
            <Box className={classes.root}>
                <Typography className={classes.intro}>
                    Upload PDFs in Contract Manager → Documents, then add them
                    here. The lease packet goes to the resident. The guarantor
                    packet goes to the guarantor and can be one document or
                    several. Application answers live in Applicant Data — not on
                    this tab.
                </Typography>
                <Typography className={classes.summary}>
                    {signablePacketsReadiness(leasePacket, guarantorPacket)}
                    {!packetOk
                        ? ' — add at least one always-include lease document'
                        : ''}
                </Typography>

                <Typography className={classes.sectionTitle}>
                    Lease Packet
                </Typography>
                <Typography className={classes.sectionHint}>
                    Ordered documents sent with Send lease package. Include or
                    exclude each PDF when Applicant Data or a qualifying method
                    matches.
                </Typography>
                <PacketEditor
                    members={leasePacket}
                    onChange={onLeasePacketChange}
                    readOnly={readOnly}
                    docs={docs}
                    docsLoading={isLoading}
                    emptyHint="No lease packet documents yet. Add the lease contract and any addenda."
                    addLabel="Add lease document"
                />

                <Typography className={classes.sectionTitle}>
                    Guarantor Packet
                </Typography>
                <Typography className={classes.sectionHint}>
                    Documents sent only to the guarantor. Usually one guaranty.
                    Leave empty when there is no guarantor path.
                </Typography>
                <PacketEditor
                    members={guarantorPacket}
                    onChange={onGuarantorPacketChange}
                    readOnly={readOnly}
                    docs={docs}
                    docsLoading={isLoading}
                    emptyHint="No guarantor packet yet. Optional — add a guaranty if that path is used."
                    addLabel="Add guarantor document"
                />
            </Box>
        </SetupArtifactShell>
    )
}
